import React, { useMemo, memo, useEffect } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Skeleton, Popover, Row, Col } from "antd";
import NoDataText from "./NoDataText";
import { isLightColor, revstackRenderPieChartOutsideLabels } from "@ts/revstack/helpers";
import Delta from "./Delta";

interface WidgetData {
    name: string;
    value: number;
    change: number;
    color: string;
}

interface RevstackBrandwiseDistributionPieWidgetProps {
    widgetData: WidgetData[];
    chartComponentRef: React.RefObject<any>;
    graphColors: string[];
    isLoading?: boolean;
    selectedBrands?: any[];
    config?: any;
    handleSetCategories?: (value: Record<string, any>[]) => void;
    selectedCategory?: string;
}

// Constants
const CHART_HEIGHT = 232;
const CHART_PADDING = 8;
const INNER_SIZE = "40%";
const DATA_LABEL_DISTANCE = -30;
const DOT_SIZE = 9;

// Memoized chart options
const createPieChartOptions = (
    widgetData: WidgetData[],
    graphColors: string[],
    showList = true,
    customTooltip = false,
    customTooltipConfig: Record<string, any> = {}
) => ({
    chart: {
        type: "pie" as const,
        height: CHART_HEIGHT,
        width: null as any, // Make chart responsive
        spacing: showList ? [0, 0, 0, 0] : [0, 0, 0, 0], // Reduced spacing for larger pie
        events: {
            render: function (this: any) {
                // Add custom outside labels when showList is false
                if (!showList) {
                    revstackRenderPieChartOutsideLabels(this, {
                        labelDistance: 10,
                        fontSize: '12px',
                        fontWeight: 'bold',
                        color: '#000',
                        debug: false
                    });
                }
            }
        }
    },
    title: {
        text: "",
    },
    tooltip: !customTooltip ? {
        pointFormat: "{series.name}: <b>{point.percentage:.1f}%</b>",
    } : {
        useHTML: true,
        backgroundColor: "#2D2D2D", // dark background
        borderRadius: 10,
        borderWidth: 0,
        shadow: false,
        style: {
            color: "#fff",
            fontFamily: "Manrope, sans-serif",
            fontSize: "13px",
            padding: 0,
        },
        formatter: function (this: Highcharts.TooltipFormatterContextObject) {
            const point = this.point as any;

            // Find the corresponding data from widgetData based on point name
            const tooltipData = widgetData.find(brand => brand.name === point.name);

            // Get the section title from the point name
            const sectionTitle = point.name?.toUpperCase() || "BRAND DATA";

            // Create rows based on available data
            const tooltipDataKey = customTooltipConfig.tooltipDataKey as keyof typeof tooltipData;


            const rows = (tooltipData && tooltipDataKey && Array.isArray(tooltipData[tooltipDataKey]))
                ? (tooltipData[tooltipDataKey] as any[]).map((item: any) => ({
                    label: item.key,
                    value: item.value,
                    change: item.change
                }))
                : [];

            let html = `
                <div style="min-width:220px;max-width:240px;padding:0;">
                    <div style="color:#6B7280;font-size:10px;font-weight:700;letter-spacing:1px;text-transform:uppercase;padding:8px 16px 4px 16px;">
                        ${sectionTitle} ${customTooltipConfig?.toolTipTitleSuffix}
                    </div>
                    <div style="padding:0 16px 8px 16px;">
            `;
            (rows ?? []).forEach((row: { label: string; value: number; change?: number }) => {
                const changeColor = typeof row.change === "number" && row.change < 0 ? "#DF2A16" : "#198754";
                const changeIcon = typeof row.change === "number" && row.change < 0 ? "▼" : "▲";
                html += `
                    <div style="display:flex;justify-content:space-between;align-items:center;margin:4px 0;">
                        <span style="color:#E0E0E0;fontSize:10px;">${row.label}</span>
                        <span>
                            <span style="fontSize:10px;color:#FFF;">${row.value?.toFixed(1) ?? ""}%</span>
                            ${row.change ? `<span style="color:${changeColor};fontSize:10px;margin-left:8px;">${changeIcon} ${Math.abs(row.change).toFixed(1)}%</span>` : ""}
                        </span>
                    </div>
                `;
            });
            html += `</div></div>`;
            return html;
        }
    },
    credits: {
        enabled: false,
    },
    plotOptions: {
        pie: {
            allowPointSelect: true,
            cursor: "pointer",
            dataLabels: {
                enabled: true,
                format: "{point.percentage:.1f}%", // Always show percentage inside
                distance: DATA_LABEL_DISTANCE, // Always inside the pie
                connectorWidth: 0, // No connector lines
                style: {
                    fontSize: "10px",
                    fontWeight: "500",
                    fontFamily: "Manrope, sans-serif",
                    textOutline: "none",
                },
                formatter: function (this: any): string {
                    const color = this.point.color;
                    const textColor = isLightColor(color) ? "#000000" : "#ffffff";
                    return `<span style="color: ${textColor}; fontSize: 10px;">${this.point.percentage.toFixed(1)}%</span>`;
                }
            },
            showInLegend: false, // Don't show in legend since we're using data labels
            innerSize: INNER_SIZE,
            borderWidth: 2,
            borderColor: "#ffffff",
        },
    },
    series: [
        {
            name: "Brands",
            colorByPoint: true,
            data: widgetData?.map((brand, index) => ({
                name: brand.name,
                y: brand.value,
                color: graphColors[index],
            })),
        },
    ],
});

const transformBrandData = (widgetData: WidgetData[], graphColors: string[]) =>
    widgetData?.map((brand, index) => ({
        name: brand.name,
        percentage: `${brand.value}%`,
        change: brand?.change ? `${Math.abs(brand.change)}%` : '-',
        dotColor: graphColors[index],
        isPositive: brand?.change ? brand?.change >= 0 : null,
    }));

const MyBrandsPopoverContent = ({ selectedBrands }: { selectedBrands: any[] }) => (
    <div
        style={{
            background: "#1E1B39",
            borderRadius: 10,
            minWidth: 280,
            maxHeight: 300,
            color: "#fff",
            padding: 0,
            fontSize: 12,
            overflow: "hidden",
        }}
    >
        {
            selectedBrands?.length > 0 && <div
                style={{
                    color: "#919191",
                    fontWeight: 600,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    borderBottom: "1px solid #2B2841",
                    padding: "10px 20px",
                }}
            >
                Selected Brand(s)
            </div>
        }
        <div
            style={{
                maxHeight: selectedBrands?.length > 0 ? 240 : 300,
                overflowY: "auto",
                borderRadius: selectedBrands?.length > 0 ? "0 0 10px 10px" : "10px",
            }}
        >
            {selectedBrands?.map((brand: any, idx: number) => (
                <div
                    className="d-flex align-items-center gap-2"
                    key={brand.name + idx}
                    style={{
                        fontWeight: 400,
                        padding: "12px 20px",
                        borderBottom:
                            idx === selectedBrands?.length - 1
                                ? "none"
                                : "1px solid #2B2841",
                    }}
                >
                    <span style={{ flex: 2 }}>{brand.name}</span>
                    <span style={{ flex: 1, textAlign: "right" }}>
                        {brand?.value ? `${Math.abs(brand.value)}%` : '-'}
                    </span>
                    <span
                        className={`d-flex align-items-center justify-content-start ${brand?.isPositive ? "text-success" : "text-danger"}`}
                        style={{
                            flex: 1,
                            fontWeight: 500,
                        }}
                    >
                        {
                            brand?.change ? (
                                <>
                                    {typeof brand?.isPositive === "boolean" && <Delta isPositive={brand?.isPositive} />}
                                    <span className="ms-2">{`${Math.abs(brand.change)}%`}</span>
                                </>
                            ) : (
                                <span className="ms-2">-</span>
                            )
                        }
                    </span>
                </div>
            ))}
        </div>
    </div>
);

const BrandListItem = memo<{
    brand: ReturnType<typeof transformBrandData>[0];
    isFirst: boolean;
    selectedBrands?: any[];
}>(({ brand, isFirst, selectedBrands }) => {
    const commonStyle = {
        padding: "8px 10px"
    }
    const content = (
        <tr
            className={`${isFirst ? "border-bottom py-3" : ""
                }`}
            style={{
                borderBottom: isFirst ? "1px solid #e8e8e8" : "none",
                padding: "8px 0"
            }}
        >
            <td className="d-flex align-items-center gap-2" style={commonStyle}>
                <div
                    className="rounded-circle"
                    style={{
                        width: `${DOT_SIZE}px`,
                        height: `${DOT_SIZE}px`,
                        backgroundColor: brand.dotColor,
                        flexShrink: 0,
                        minWidth: `${DOT_SIZE}px`,
                        minHeight: `${DOT_SIZE}px`,
                    }}
                />
                <span className="fw-medium">{brand.name}</span>
            </td>
            <td className="fw-medium text-end" style={commonStyle}>
                {brand.percentage || "-"}
            </td>
            <td
                className={`d-flex align-items-center fw-medium ${brand?.isPositive ? "text-success" : "text-danger"}`}
                style={{
                    ...commonStyle,
                    fontSize: 12,
                }}
            >
                {typeof brand?.isPositive === "boolean" && <Delta isPositive={brand?.isPositive} />} <span className="ms-1">{brand.change || "-"}</span>
            </td>
        </tr>
    );

    if (isFirst && selectedBrands) {
        return (
            <Popover
                content={<MyBrandsPopoverContent selectedBrands={selectedBrands} />}
                trigger="hover"
                placement="top"
            >
                {content}
            </Popover>
        );
    }

    return content;
});

const RevstackBrandwiseDistributionPieWidget: React.FC<
    RevstackBrandwiseDistributionPieWidgetProps
> = ({ widgetData, chartComponentRef, graphColors, isLoading = false, selectedBrands, config, handleSetCategories, selectedCategory }) => {

    useEffect(() => {

        const filterOptions = widgetData?.map((item: any) => ({ label: item.name, value: item.name })) || [];

        handleSetCategories && handleSetCategories([{ label: `All ${config?.filterKey}`, value: "all" }, ...filterOptions]);
    }, []);


    const showNoData = !widgetData?.length;
    const chartHeight = CHART_HEIGHT + CHART_PADDING;

    // Memoize expensive calculations
    const pieChartOptions = useMemo(
        () => createPieChartOptions(widgetData, graphColors, config?.showList, config?.customTooltip, config.customToolTipConfig),
        [widgetData, graphColors, config?.showList, config?.customTooltip]
    );

    const brandData = useMemo(
        () => transformBrandData(widgetData, graphColors),
        [widgetData, graphColors]
    );

    if (isLoading) {
        return (
            <Skeleton.Node
                className="w-100 mt-2"
                active
                style={{ height: chartHeight }}
            />
        );
    }

    if (showNoData) {
        return (
            <div
                className="d-flex align-items-center justify-content-center mt-1"
                style={{ height: chartHeight }}
            >
                <NoDataText />
            </div>
        );
    }

    return (
        <Row className="d-flex h-100 px-2">
            <Col span={config?.showList ? 10 : 24}
                ref={chartComponentRef}
                className="d-flex align-items-center justify-content-center ps-2"
                style={{
                    minWidth: 0, // Allow column to shrink
                    overflow: 'hidden' // Prevent overflow
                }}
            >
                <div style={{
                    width: '100%',
                    maxWidth: '100%',
                    height: CHART_HEIGHT
                }}>
                    <HighchartsReact
                        highcharts={Highcharts}
                        options={pieChartOptions}
                        ref={chartComponentRef}
                    />
                </div>
            </Col>
            {
                config?.showList && (
                    <Col span={14} className="d-flex align-items-center justify-content-end ps-2">
                        <table className="w-100 mb-0">
                            <tbody>
                                {brandData?.map((brand, index) => (
                                    <BrandListItem
                                        key={`${brand.name}-${index}`}
                                        brand={brand}
                                        isFirst={index === 0}
                                        selectedBrands={index === 0 ? selectedBrands : undefined}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </Col>
                )
            }
        </Row>
    );
};

export default memo(RevstackBrandwiseDistributionPieWidget);